import { Space,Tag,Typography } from "../../../../../../shared/frontend/ui";
import { spacing,typography } from "../../app/design-system";
import { useI18n } from "../../app/i18n/useI18n";
import { formatDateOnly,formatDateTimeIso,formatMoney,formatNumber } from "../../app/units/format";
import type { UnitsSettings } from "../../app/units/units";
import { restPeriodFeeUnitText } from "./restPeriodFeeUtils";

/** Entspricht `RestPeriodPreviewValueKind` im Backend. */
const VALUE_KIND = { Text: 0, Money: 1, Date: 2, Boolean: 3, Number: 4, CycleUnit: 5, Translation: 6, Timestamp: 7 } as const;

export type RestPeriodPreviewValue = { kind: typeof VALUE_KIND[keyof typeof VALUE_KIND]; text?: string | null; number?: number | null; date?: string | null; flag?: boolean | null; time?: string | null; referenceId?: string | null };
export type RestPeriodPreviewChange = {
  area: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7; id: string; name: string; kind: 0 | 1 | 2;
  fields: Array<{ labelKey: string; before?: RestPeriodPreviewValue | null; after?: RestPeriodPreviewValue | null }>;
};
const areaKeys = ["Rest period", "Contract package", "Contract", "Rest period preview bookings", "Contribution correction", "Payments", "Wallets", "Rest period preview wallet movements"];
const kindKeys = ["Rest period preview row new", "Rest period preview row changed", "Rest period preview row removed"];

export function RestPeriodPreviewChangeList({ changes, units }: { changes: RestPeriodPreviewChange[]; units: UnitsSettings }) {
  const { text } = useI18n();
  const valueText = (value?: RestPeriodPreviewValue | null): string => {
    if (!value) return "—";
    if (value.kind === 1) return value.number == null ? "—" : formatMoney(value.number, units);
    if (value.kind === VALUE_KIND.Date) return value.date ? formatDateOnly(value.date, units) : "—";
    if (value.kind === VALUE_KIND.Boolean) return value.flag == null ? "—" : text(value.flag ? "Yes" : "No");
    if (value.kind === VALUE_KIND.Number) return value.number == null ? "—" : formatNumber(value.number, units);
    if (value.kind === VALUE_KIND.CycleUnit) return value.number == null ? "—" : restPeriodFeeUnitText(value.number, text);
    if (value.kind === VALUE_KIND.Translation) return value.text ? text(value.text) : "—";
    if (value.kind === VALUE_KIND.Timestamp) return value.time ? formatDateTimeIso(value.time, units) : "—";
    return value.text || "—";
  };
  if (changes.length === 0) return null;
  return (
    <Space direction="vertical" size={spacing.sm} style={{ width: "100%" }}>
      <Typography.Text type="secondary">{text("Rest period preview further changes")}</Typography.Text>
      {changes.map(change => (
        <Space key={`${change.area}-${change.id}`} direction="vertical" size={spacing.xs} style={{ width: "100%" }}>
          <Space wrap>
            <Typography.Text strong>{text(areaKeys[change.area] ?? "Changes")}{change.name ? ` · ${change.name}` : ""}</Typography.Text>
            <Tag>{text(kindKeys[change.kind] ?? "Rest period preview row changed")}</Tag>
          </Space>
          {change.fields.map(field => (
            <Typography.Text key={field.labelKey} style={{ fontSize: typography.fontSize.sm, overflowWrap: "anywhere" }}>
              {text(field.labelKey)}: {valueText(field.before)} → {valueText(field.after)}
              {field.before?.referenceId && field.after?.referenceId && field.before.referenceId !== field.after.referenceId
                ? ` · ${text("Rest period preview assignment changed")}` : ""}
            </Typography.Text>
          ))}
        </Space>
      ))}
    </Space>
  );
}
